import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import ProjectsListModal from '../dashboard/ProjectsListModal';
import SuppliersListModal from '../dashboard/SuppliersListModal';
import BestMixturesCard from '../dashboard/BestMixturesCard';
import BestMixturesModal from '../dashboard/BestMixturesModal';
import api from '../../services/api';
import { useSidebar } from '../../contexts/SidebarContext';

export const RightSidebar = ({ topCountries = [], topCustomers = [], topProducts = [] }) => {
  const { isRightExpanded, setIsRightExpanded } = useSidebar();
  const [isProjectsModalOpen, setIsProjectsModalOpen] = useState(false);
  const [isSuppliersModalOpen, setIsSuppliersModalOpen] = useState(false);
  const [isMixturesModalOpen, setIsMixturesModalOpen] = useState(false);
  const [refreshedProjects, setRefreshedProjects] = useState([]);
  const [refreshedSuppliers, setRefreshedSuppliers] = useState([]);
  const [refreshedMixtures, setRefreshedMixtures] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Function to fetch fresh data when modals are opened
  const fetchFreshData = async () => {
    try {
      setIsLoading(true);

      // Determine which data to fetch based on which modal is open
      const promises = [];

      if (isProjectsModalOpen) {
        promises.push(
          api.get('/api/delivery-notes/largest-projects/no-cache')
            .then(response => {
              if (response.data.success && response.data.data) {
                setRefreshedProjects(response.data.data);
              }
            })
        );
      }

      if (isSuppliersModalOpen) {
        promises.push(
          api.get('/api/delivery-notes/largest-suppliers/no-cache')
            .then(response => {
              if (response.data.success && response.data.data) {
                setRefreshedSuppliers(response.data.data);
              }
            })
        );
      }

      if (isMixturesModalOpen) {
        promises.push(
          api.get('/api/delivery-notes/all-mixtures/no-cache')
            .then(response => {
              if (response.data.success && response.data.data) {
                setRefreshedMixtures(response.data.data);
              }
            })
        );
      }

      // Wait for all requests to complete
      await Promise.all(promises);
    } catch (error) {
      console.error('Error fetching fresh data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch fresh data when any modal is opened
  useEffect(() => {
    if (isProjectsModalOpen || isSuppliersModalOpen || isMixturesModalOpen) {
      fetchFreshData();
    }
  }, [isProjectsModalOpen, isSuppliersModalOpen, isMixturesModalOpen]);
  // Helper function to get logo path
  const getLogoPath = (companyName) => {
    // For postal code + city format, use the postal code for the logo
    if (/^\d{5}\s/.test(companyName)) {
      const postalCode = companyName.substring(0, 5);
      return `/logos/${postalCode.toLowerCase()}.png`;
    }

    // Otherwise, extract the first word before any special characters or spaces
    const baseName = companyName.split(/[\s,-]+/)[0];
    return `/logos/${baseName.toLowerCase()}.png`;
  };

  // Helper function to get project logo
  const getProjectLogoPath = (projectName) => {
    // Extract the highway number (e.g., "A65" from "A65 FDE AS Haßloch")
    const match = projectName.match(/A\d+/);
    const projectNumber = match ? match[0] : "placeholder";
    return `/logos/${projectNumber}.png`;
  };

  // Helper function to format CO2 text with subscript
  const formatCO2 = (text) => {
    return text.replace('CO2e/t', 'CO<sub>2</sub>e/t');
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  return (
    <div className={`fixed right-0 top-0 h-full bg-white border-l border-gray-200 transition-all duration-300 ease-in-out z-40 ${
      isRightExpanded ? 'w-64 p-4' : 'w-12 p-0'
    } overflow-y-auto`}>
      {/* Toggle Button */}
      <button
        onClick={() => setIsRightExpanded(!isRightExpanded)}
        className={`flex items-center justify-center w-8 h-8 rounded-md text-gray-600 hover:bg-green-50 hover:text-green-600 transition-all duration-300 ${
          isRightExpanded ? 'mb-2' : 'mx-auto mt-4'
        }`}
        title={isRightExpanded ? 'Einklappen' : 'Ausklappen'}
      >
        {isRightExpanded ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
      </button>

      {/* Content - only visible when expanded */}
      {isRightExpanded && (
      <>
      {/* Best Asphalt Mixtures */}
      <BestMixturesCard
        mixtures={topProducts}
        onShowAll={() => setIsMixturesModalOpen(true)}
      />

      {/* Top Countries */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Größte Projekte</h2>
          <button
            className="text-sm text-green-600 hover:text-green-700"
            onClick={() => setIsProjectsModalOpen(true)}
          >
            Alle anzeigen
          </button>
        </div>
        <div className="space-y-4">
          {/* Only show top 3 projects in the sidebar */}
          {topCountries.slice(0, 3).map((item, index) => (
            <div key={index} className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center overflow-hidden flex-shrink-0">
                  <img
                    src={getProjectLogoPath(item.name)}
                    alt={item.name}
                    className="w-6 h-6 object-contain"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = `/api/placeholder/24/24`;
                    }}
                  />
                </div>
                <div>
                  <div>
                    {(() => {
                      // Match highway number followed by FDE or FDI
                      const match = item.name.match(/(A\d+\s+(?:FDE|FDI))/i);
                      if (match) {
                        // Use the first capture group as the first part (A65 FDE)
                        const firstPart = match[1];
                        // Use everything after the project code as the second part
                        const secondPartIndex = item.name.indexOf(firstPart) + firstPart.length;
                        const secondPart = item.name.substring(secondPartIndex);
                        return (
                          <>
                            <div className="font-medium">{firstPart}</div>
                            <div className="text-xs text-gray-500">{secondPart}</div>
                          </>
                        );
                      } else {
                        return <div className="font-medium">{item.name}</div>;
                      }
                    })()}
                  </div>
                  <div className="text-sm text-green-600" dangerouslySetInnerHTML={{ __html: formatCO2(`${item.purchases} kg CO2e/t`) }} />
                </div>
              </div>
              <div className="font-medium whitespace-nowrap">{item.amount}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Top Customers */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Größte Zulieferer</h2>
          <button
            className="text-sm text-green-600 hover:text-green-700"
            onClick={() => setIsSuppliersModalOpen(true)}
          >
            Alle anzeigen
          </button>
        </div>
        <div className="space-y-4">
          {/* Only show top 3 suppliers in the sidebar */}
          {topCustomers.slice(0, 3).map((customer, index) => (
            <div key={index} className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center overflow-hidden flex-shrink-0">
                  <img
                    src="/logos/BAG.png"
                    alt={customer.name}
                    className="w-6 h-6 object-contain"
                    onError={(e) => {
                      e.target.onerror = null;
                      if (customer.avatar) {
                        e.target.src = customer.avatar;
                      } else {
                        e.target.src = `/api/placeholder/24/24`;
                      }
                    }}
                  />
                </div>
                <div>
                  <div>
                    {(() => {
                      // Split postal code and city name
                      const match = customer.name.match(/^(\d{5})\s+(.+)$/);
                      if (match) {
                        // If it matches the pattern "12345 City"
                        return (
                          <>
                            <div className="font-medium">{match[1]}</div>
                            <div className="font-medium">{match[2]}</div>
                          </>
                        );
                      } else {
                        return <div className="font-medium">{customer.name}</div>;
                      }
                    })()}
                  </div>
                  <div className="text-sm text-green-600" dangerouslySetInnerHTML={{ __html: formatCO2(`${customer.purchases} kg CO2e/t`) }} />
                </div>
              </div>
              <div className="font-medium whitespace-nowrap">{customer.amount}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Projects List Modal */}
      <ProjectsListModal
        isOpen={isProjectsModalOpen}
        onClose={() => setIsProjectsModalOpen(false)}
        projects={refreshedProjects.length > 0 ? refreshedProjects : topCountries}
        isLoading={isLoading}
      />

      {/* Suppliers List Modal */}
      <SuppliersListModal
        isOpen={isSuppliersModalOpen}
        onClose={() => setIsSuppliersModalOpen(false)}
        suppliers={refreshedSuppliers.length > 0 ? refreshedSuppliers : topCustomers}
        isLoading={isLoading}
      />

      {/* Best Mixtures Modal */}
      <BestMixturesModal
        isOpen={isMixturesModalOpen}
        onClose={() => setIsMixturesModalOpen(false)}
        mixtures={refreshedMixtures.length > 0 ? refreshedMixtures : topProducts}
        isLoading={isLoading}
      />
      </>
      )}
    </div>
  );
};

